import { useCallback, useEffect, useRef, useState } from "react";
import {
  loadBodyPartSettings,
  saveBodyPartSetting,
} from "../../services/scan-storage";

// the stream must be at least full-HD
const WIDTH_MIN = 1920;
const HEIGHT_MIN = 1080;
const WIDTH_IDEAL = 1920;
const HEIGHT_IDEAL = 1080;
// longest side of a captured frame. A phone may ignore the ideal stream size
// above and hand back much bigger frames (4K and up on some Android models);
// so we may need to scale it down.
const CAPTURE_MAX_SIDE = 1920;
const CAPTURE_QUALITY = 0.85;
// a capture slower than this is reported to the console, for the debugging
// performance purpose.
const SLOW_CAPTURE_MS = 600;
// how long to wait for the encoder worker before giving up on it. Well above
// any real encode (those run in milliseconds), so reaching it means the worker
// is wedged rather than slow.
const ENCODE_TIMEOUT_MS = 5000;

// Encapsulates the live-camera lifecycle for the scanner: selecting and opening
// a stream, exposing/toggling the torch, switching between front/rear cameras,
// and capturing frames. The consumer only deals with the returned API and never
// touches the underlying MediaStream / <video> element directly.
//
// Torch note: some phones (e.g. Samsung) have multiple rear cameras. And auto
// selection may pick one that doesn't support the torch. Therefore we probe
// once for a torch-capable device and open it by deviceId.
//
// bodyPart: restore (and keep remembering) the user's saved camera facing and
// torch preferences for that body part — the 3D scan behavior; defaultFront
// starts with the front camera until the user picks one. When omitted
// (the image capture), always start with the rear camera and the torch off.
//
// active: Setting this off (i.e., false) prevents activating the camera and
// draining device resources.
export function useCamera({
  bodyPart,
  active = true,
  torchAllowed = true,
  defaultFront = false,
} = {}) {
  const rememberSettings = Boolean(bodyPart);

  // Ref values
  const torchAllowedRef = useRef(torchAllowed);
  useEffect(() => {
    torchAllowedRef.current = torchAllowed;
  }, [torchAllowed]);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const trackRef = useRef(null);

  // cached deviceId of the torch-capable rear camera.
  // undefined = not probed yet, null = probed, none found, string = deviceId.
  const rearTorchDeviceRef = useRef(undefined);

  const [error, setError] = useState(null);
  const [torchOn, setTorchOn] = useState(false);
  const [torchSupported, setTorchSupported] = useState(false);
  // latest torchOn, so the visibility handler below can read it without being
  // re-registered on every toggle.
  const torchOnRef = useRef(false);
  // true while the one-time rear-camera probe runs (can take a few seconds on
  // some phones).
  const [preparing, setPreparing] = useState(false);
  // which camera to use: "environment" (rear) or "user" (front); the user's
  // last choice is restored (when remembered).
  const [facingMode, setFacingMode] = useState(() => {
    if (!rememberSettings) return "environment";
    const saved = loadBodyPartSettings(bodyPart).facingMode;
    if (saved) return saved === "user" ? "user" : "environment";
    return defaultFront ? "user" : "environment";
  });
  // bumped to reopen the camera after the phone ended the track (see the
  // visibility handler below).
  const [restartKey, setRestartKey] = useState(0);
  // bumped on every return to the foreground to remount the <video>: to make
  // sure the video element is correctly laid out.
  const [videoKey, setVideoKey] = useState(0);

  // (re)start the camera whenever the selected facing mode changes.
  useEffect(() => {
    let cancelled = false;

    // find the rear camera that reports torch support, opening candidates one
    // at a time. Returns a deviceId, or null if none expose the torch.
    async function findRearTorchDevice() {
      try {
        // wait until the user grants camera permission first.
        const perm = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: false,
        });
        perm.getTracks().forEach((t) => t.stop());

        const devices = await navigator.mediaDevices.enumerateDevices();
        const cams = devices.filter((d) => d.kind === "videoinput");
        for (const cam of cams) {
          let probe = null;
          try {
            probe = await navigator.mediaDevices.getUserMedia({
              video: { deviceId: { exact: cam.deviceId } },
              audio: false,
            });
            const t = probe.getVideoTracks()[0];
            const caps = t.getCapabilities ? t.getCapabilities() : {};
            const rear = (caps.facingMode || []).includes("environment");
            if (rear && caps.torch) return cam.deviceId;
          } catch {
            // skip cameras that fail to open
          } finally {
            if (probe) probe.getTracks().forEach((x) => x.stop());
          }
        }
      } catch {
        // enumerateDevices failed — fall back to facingMode
      }
      return null;
    }

    async function startCamera() {
      try {
        // for the rear camera, prefer the (cached) torch-capable device; probe
        // for it once. Front camera and the fallback use facingMode.
        let videoConstraints = {
          facingMode: { ideal: facingMode },
          width: { min: WIDTH_MIN, ideal: WIDTH_IDEAL },
          height: { min: HEIGHT_MIN, ideal: HEIGHT_IDEAL },
        };
        if (facingMode === "environment") {
          if (rearTorchDeviceRef.current === undefined) {
            setPreparing(true);
            try {
              rearTorchDeviceRef.current = await findRearTorchDevice();
            } finally {
              if (!cancelled) setPreparing(false);
            }
            if (cancelled) return;
          }
          if (rearTorchDeviceRef.current) {
            videoConstraints = {
              deviceId: { exact: rearTorchDeviceRef.current },
              width: { min: WIDTH_MIN, ideal: WIDTH_IDEAL },
              height: { min: HEIGHT_MIN, ideal: HEIGHT_IDEAL },
            };
          }
        }

        // ask for a full-HD stream from the selected camera.
        const stream = await navigator.mediaDevices.getUserMedia({
          video: videoConstraints,
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        const track = stream.getVideoTracks()[0];
        trackRef.current = track;
        // Torch support is known up front: the rear torch device is only set
        // when the probe confirmed `caps.torch`, so opening it means the torch
        // is available. Default the torch to ON whenever that camera starts,
        // unless the user turned it off on a previous scan (with settings not
        // remembered, it always starts off).
        const torchAvailable =
          facingMode === "environment" && Boolean(rearTorchDeviceRef.current);
        setTorchSupported(torchAvailable);
        if (
          torchAvailable &&
          torchAllowedRef.current &&
          rememberSettings &&
          loadBodyPartSettings(bodyPart).torchOn !== false
        ) {
          try {
            await track.applyConstraints({ advanced: [{ torch: true }] });
            setTorchOn(true);
          } catch {
            setTorchOn(false);
          }
        } else {
          setTorchOn(false);
        }
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          // after a resume the element can be left paused; autoplay only
          // covers the first attach.
          const played = videoRef.current.play();
          if (played && played.catch) played.catch(() => {});
        }
        setError(null);
      } catch (err) {
        console.error("Camera error:", err.message || err);
        setError(
          "Unable to access the camera. Check camera permissions, " +
            "close other apps or tabs using the camera, and try again.",
        );
      }
    }

    if (active) startCamera();

    return () => {
      cancelled = true;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
      trackRef.current = null;
    };
  }, [facingMode, rememberSettings, bodyPart, restartKey, active]);

  useEffect(() => {
    torchOnRef.current = torchOn;
  }, [torchOn]);

  // `activeRef` mirroring the behavior of `torchOnRef`
  const activeRef = useRef(active);
  useEffect(() => {
    activeRef.current = active;
  }, [active]);

  // Sending the page to the background makes the phone release the camera: the
  // torch goes off in hardware while our state (and so the torch button) still
  // says on, and after a longer while the video track is ended outright,
  // leaving the preview frozen. Put both back on returning to the foreground.
  useEffect(() => {
    let timer = null;

    async function handleResume() {
      // Do not trigger `setRestartKey`
      // if the device goes to background during tutorial.
      // (i.e., the scanner is not yet activated.)
      if (!activeRef.current) {
        return;
      }
      setVideoKey((key) => key + 1);
      const track = trackRef.current;

      // the track is gone — reopen the camera (the torch-capable device is
      // already cached, so this doesn't re-probe). Starting the stream applies
      // the torch preference again, so there is nothing more to do here.
      if (!track || track.readyState !== "live") {
        setRestartKey((key) => key + 1);
        return;
      }
      // the track survived, but the element itself may have been paused while
      // hidden.
      if (videoRef.current && videoRef.current.paused) {
        const played = videoRef.current.play();
        if (played && played.catch) played.catch(() => {});
      }
      if (!torchOnRef.current) return;
      try {
        // the track may still believe the torch is on, which makes applying
        // torch:true a no-op — go through off first to force the change.
        await track.applyConstraints({ advanced: [{ torch: false }] });
        await track.applyConstraints({ advanced: [{ torch: true }] });
        const settings = track.getSettings ? track.getSettings() : {};
        // if it can't be turned back on, reflect the real (off) state so the
        // button doesn't lie.
        if (settings.torch === false) setTorchOn(false);
      } catch {
        setTorchOn(false);
      }
    }

    function handleVisibility() {
      if (document.visibilityState !== "visible") return;
      // the camera needs a moment to resume before it accepts constraints.
      if (timer) clearTimeout(timer);
      timer = setTimeout(handleResume, 400);
    }

    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      if (timer) clearTimeout(timer);
    };
  }, []);

  // attach the live stream to the <video> whenever it (re)mounts.
  const attachVideo = useCallback((node) => {
    videoRef.current = node;
    if (node && streamRef.current) {
      node.srcObject = streamRef.current;
      const played = node.play();
      if (played && played.catch) played.catch(() => {});
    }
  }, []);

  const toggleTorch = useCallback(async (on) => {
    const track = trackRef.current;
    if (!track) return;
    try {
      await track.applyConstraints({ advanced: [{ torch: on }] });
      setTorchOn(on);
    } catch {
      // torch not supported / failed — leave state unchanged
    }
  }, []);

  // toggle between the rear and front camera (remembered for the next scan).
  const switchCamera = useCallback(() => {
    setFacingMode((mode) => {
      const next = mode === "environment" ? "user" : "environment";
      if (rememberSettings) saveBodyPartSetting(bodyPart, "facingMode", next);
      return next;
    });
  }, [rememberSettings, bodyPart]);

  // reusable capture canvas and its context.
  const captureCanvasRef = useRef(null);
  const captureCtxRef = useRef(null);
  // ImageCapture of the live track, to grab frames from the camera itself.
  const imageCaptureRef = useRef(null);
  const imageCaptureTrackRef = useRef(null);
  // the JPEG encoder worker: undefined = not started, null = unavailable.
  const encodeWorkerRef = useRef(undefined);
  const encodeSeqRef = useRef(0);
  const encodePendingRef = useRef(new Map());

  // Give up on the worker: whatever frame is still waiting on it is lost (its
  // bitmap was transferred, so it cannot be encoded here instead), and every
  // later frame goes down the main-thread path.
  const retireWorker = useCallback(() => {
    const worker = encodeWorkerRef.current;
    encodeWorkerRef.current = null; // null, so it is not started again
    if (worker) worker.terminate();
    encodePendingRef.current.forEach((resolve) => resolve(null));
    encodePendingRef.current.clear();
  }, []);

  // the worker encoding captured frames, started on the first capture. Returns
  // null where it can't run (no Worker/OffscreenCanvas, or it failed) — the
  // caller then encodes on the main thread instead.
  const encodeWorker = useCallback(() => {
    if (encodeWorkerRef.current !== undefined) return encodeWorkerRef.current;
    encodeWorkerRef.current = null;
    if (
      typeof Worker === "undefined" ||
      typeof OffscreenCanvas === "undefined"
    ) {
      return null;
    }
    try {
      const worker = new Worker(new URL("./encode-worker.js", import.meta.url));
      worker.onmessage = (event) => {
        const { id, blob } = event.data;
        const resolve = encodePendingRef.current.get(id);
        if (!resolve) return;
        encodePendingRef.current.delete(id);
        resolve(blob || null);
      };
      // the worker died — fall back to the main thread from here on.
      worker.onerror = retireWorker;
      encodeWorkerRef.current = worker;
    } catch {
      encodeWorkerRef.current = null;
    }
    return encodeWorkerRef.current;
  }, [retireWorker]);

  // stop the worker with the camera.
  useEffect(() => {
    const pending = encodePendingRef.current;
    return () => {
      if (encodeWorkerRef.current) encodeWorkerRef.current.terminate();
      encodeWorkerRef.current = undefined;
      pending.clear();
    };
  }, []);

  // Grab the current frame straight from the video track, bypassing the
  // <video> element. In fullscreen, Android promotes the preview to a hardware
  // overlay, and copying a frame back out of that surface can stall for
  // seconds; the track itself is not affected. Returns null when the browser
  // has no ImageCapture, or grabbing failed — the caller then falls back to
  // the <video> element.
  const grabTrackFrame = useCallback(async () => {
    const track = trackRef.current;
    if (!track || track.readyState !== "live" || !window.ImageCapture) {
      return null;
    }
    try {
      if (imageCaptureTrackRef.current !== track) {
        imageCaptureRef.current = new window.ImageCapture(track);
        imageCaptureTrackRef.current = track;
      }
      return await imageCaptureRef.current.grabFrame();
    } catch {
      imageCaptureRef.current = null;
      imageCaptureTrackRef.current = null;
      return null;
    }
  }, []);

  // grab the current camera frame as a JPEG Blob, stored as landscape (as the
  // server expects) and scaled down to CAPTURE_MAX_SIDE.
  const captureFrame = useCallback(async () => {
    const started = performance.now();
    const video = videoRef.current;
    // a frame from the track when the browser allows it, the preview
    // otherwise. Both carry the same image; only the track's is already in
    // the camera's own (landscape) orientation.
    const grabbed = await grabTrackFrame();
    const grabbedAt = performance.now();
    const source = grabbed || video;
    const sourceWidth = grabbed ? grabbed.width : video && video.videoWidth;
    const sourceHeight = grabbed ? grabbed.height : video && video.videoHeight;
    if (!source || !sourceWidth) {
      if (grabbed) grabbed.close();
      return null;
    }
    const scale = Math.min(
      1,
      CAPTURE_MAX_SIDE / Math.max(sourceWidth, sourceHeight),
    );
    // the drawn size, and whether it still has to be turned a quarter turn to
    // be stored as landscape (as the server expects).
    const w = Math.round(sourceWidth * scale);
    const h = Math.round(sourceHeight * scale);
    const rotate = h > w;
    let canvas = captureCanvasRef.current;
    if (!canvas) {
      canvas = document.createElement("canvas");
      captureCanvasRef.current = canvas;
      // Keep the canvas in CPU memory.
      captureCtxRef.current = canvas.getContext("2d", {
        willReadFrequently: true,
      });
    }
    // hand the frame to the worker when it can take it: the encode then runs
    // off the main thread, on its own canvas.
    const worker = grabbed ? encodeWorker() : null;
    if (worker) {
      const id = (encodeSeqRef.current += 1);
      const blob = await new Promise((resolve) => {
        // a worker that never answers would leave this promise — and with it
        // the whole capture run — hanging, so stop waiting at some point.
        const timer = setTimeout(() => {
          console.warn(`encode worker gave up after ${ENCODE_TIMEOUT_MS}ms`);
          retireWorker();
        }, ENCODE_TIMEOUT_MS);
        encodePendingRef.current.set(id, (result) => {
          clearTimeout(timer);
          resolve(result);
        });
        // the bitmap is transferred, so the worker owns (and closes) it.
        worker.postMessage(
          {
            id,
            bitmap: grabbed,
            width: w,
            height: h,
            rotate,
            quality: CAPTURE_QUALITY,
          },
          [grabbed],
        );
      });
      const done = performance.now();
      if (done - started > SLOW_CAPTURE_MS) {
        console.warn(
          `slow capture: ${Math.round(done - started)}ms ` +
            `(grab ${Math.round(grabbedAt - started)}ms, ` +
            `worker ${Math.round(done - grabbedAt)}ms) from track ` +
            `${sourceWidth}x${sourceHeight} at ${rotate ? h : w}x${rotate ? w : h}`,
        );
      }
      return blob;
    }

    const canvasWidth = rotate ? h : w;
    const canvasHeight = rotate ? w : h;
    if (canvas.width !== canvasWidth || canvas.height !== canvasHeight) {
      canvas.width = canvasWidth;
      canvas.height = canvasHeight;
    }
    const ctx = captureCtxRef.current;
    // the canvas is reused, so reset the transform before applying the rotation
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    if (rotate) {
      ctx.translate(0, w);
      ctx.rotate(-Math.PI / 2);
    }
    ctx.drawImage(source, 0, 0, w, h);
    if (grabbed) grabbed.close();
    const drawn = performance.now();
    // toBlob copies the bitmap at call time, so the canvas can be safely
    // reused for the next frame while this one is still encoding.
    return await new Promise((resolve) =>
      canvas.toBlob(
        (blob) => {
          const done = performance.now();
          if (done - started > SLOW_CAPTURE_MS) {
            console.warn(
              `slow capture: ${Math.round(done - started)}ms ` +
                `(grab ${Math.round(grabbedAt - started)}ms, ` +
                `draw ${Math.round(drawn - grabbedAt)}ms, ` +
                `encode ${Math.round(done - drawn)}ms) from ` +
                `${grabbed ? "track" : "preview"} ` +
                `${sourceWidth}x${sourceHeight} ` +
                `at ${canvas.width}x${canvas.height}`,
            );
          }
          resolve(blob);
        },
        "image/jpeg",
        CAPTURE_QUALITY,
      ),
    );
  }, [grabTrackFrame, encodeWorker, retireWorker]);

  return {
    attachVideo,
    videoKey,
    error,
    preparing,
    facingMode,
    switchCamera,
    torchOn,
    torchSupported,
    toggleTorch,
    captureFrame,
  };
}
